import { Controller } from 'react-hook-form';
import type { Control, FieldPath, FieldValues } from 'react-hook-form';
import { Field, FieldError, FieldLabel } from '@/shared/components/ui/field';
import { Textarea } from '@/shared/components/ui/textarea';
import { FloatingLabelTextarea } from './FloatingLabelTextarea';
import type { FloatingInputVariant } from './FormFieldInput';

interface FormFieldTextareaProps<T extends FieldValues> {
  control: Control<T>;
  name: FieldPath<T>;
  label: string;
  placeholder?: string;
  rows?: number;
  disabled?: boolean;
  className?: string;
  'data-testid'?: string;
  floatingLabel?: boolean;
  floatingVariant?: FloatingInputVariant;
  /**
   * Hide the visible label. For a field whose row already renders its own label —
   * the convert-to-task dialog does — this avoids showing two.
   *
   * The control keeps its accessible name: `label` is emitted as `aria-label`, so
   * the field is named even when nothing outside supplies one. A wrapper's own
   * `<label htmlFor>` stays valid alongside it; both carry the same text.
   */
  hideLabel?: boolean;
}

export function FormFieldTextarea<T extends FieldValues>({
  control,
  name,
  label,
  placeholder,
  rows = 4,
  disabled = false,
  className,
  'data-testid': testId,
  floatingLabel = false,
  floatingVariant = 'default',
  hideLabel = false,
}: FormFieldTextareaProps<T>) {
  // Only when the visible label is actually suppressed. The floating variant renders
  // its own label, so it needs no substitute.
  const ariaLabel = hideLabel && !floatingLabel ? label : undefined;

  return (
    <Controller
      name={name}
      control={control}
      render={({ field, fieldState }) => (
        <Field data-invalid={fieldState.invalid}>
          {!floatingLabel && !hideLabel && <FieldLabel htmlFor={name}>{label}</FieldLabel>}

          {floatingLabel ? (
            <FloatingLabelTextarea
              {...field}
              id={name}
              label={label}
              variant={floatingVariant}
              rows={rows}
              disabled={disabled}
              hasValue={field.value !== '' && field.value !== undefined && field.value !== null}
              invalid={fieldState.invalid}
              value={(field.value as string) ?? ''}
              className={className}
              data-testid={testId}
            />
          ) : (
            <Textarea
              {...field}
              id={name}
              placeholder={placeholder}
              rows={rows}
              disabled={disabled}
              aria-invalid={fieldState.invalid}
              aria-label={ariaLabel}
              value={(field.value as string) ?? ''}
              className={className}
              data-testid={testId}
            />
          )}

          {fieldState.invalid && <FieldError errors={[fieldState.error]} />}
        </Field>
      )}
    />
  );
}
